import { CartItemType } from '../../actions/shop';
import { MAX_ENFANTS } from './camp-de-jour-constants';

/**
 * Returns a new errors object with the given fieldId key removed.
 * @param {Object} prev - Current errors state
 * @param {string} fieldId - Field key to clear
 * @returns {Object} New errors object without the field
 */
export function clearFieldError(prev, fieldId) {
  const next = { ...prev };
  delete next[fieldId];
  return next;
}

/**
 * Validates the camp de jour step: nombreEnfants and per-child week selections.
 * @param {Object} params
 * @param {boolean} params.hasCampJourInCart
 * @param {string} params.nombreEnfants
 * @param {Object} params.enfantsSelections - { [childIndex]: { [weekId]: { selected, trajet } } }
 * @param {string} params.language
 * @param {Object} params.t - Translation keys (nombreEnfantsRequired, nombreEnfantsInvalid, nombreEnfantsInvalidMax, panierCampJourRequired)
 * @returns {{ errors: Object, isValid: boolean }}
 */
export function validateCampDeJourStep({ hasCampJourInCart, nombreEnfants, enfantsSelections, language, t }) {
  const errors = {};

  if (!nombreEnfants || nombreEnfants.toString().trim() === '') {
    errors.nombreEnfants = t.nombreEnfantsRequired;
  } else {
    const num = parseInt(nombreEnfants, 10);
    if (isNaN(num) || num < 1) {
      errors.nombreEnfants = t.nombreEnfantsInvalid;
    } else if (num > MAX_ENFANTS) {
      errors.nombreEnfants = t.nombreEnfantsInvalidMax;
    } else {
      if (!hasCampJourInCart) {
        errors.panier = t.panierCampJourRequired;
      }
      for (let i = 0; i < num; i++) {
        const childSelections = enfantsSelections[i] || {};
        const hasValidSelection = Object.values(childSelections).some(
          week => week && week.selected && week.trajet
        );
        if (!hasValidSelection) {
          const fieldId = `enfant${i + 1}_semaines`;
          errors[fieldId] = language === 'fr'
            ? `Veuillez sélectionner au moins une semaine avec un trajet pour l'enfant ${i + 1}`
            : `Please select at least one week with a route for child ${i + 1}`;
        }
      }
    }
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0
  };
}

/**
 * Builds initial enfantsSelections from cart (and optional formData).
 * @param {Object} params
 * @param {number} params.numEnfants
 * @param {Array} params.cart - Shop cart items
 * @param {Object} params.formData - Current form data (optional fallback/merge)
 * @returns {Object} { [childIndex]: { [weekId]: { selected, trajet } } }
 */
export function restoreSelectionsFromCart({ numEnfants, cart, formData }) {
  const result = {};
  const safeCart = Array.isArray(cart) ? cart : [];
  const n = Math.max(0, parseInt(numEnfants, 10) || 0);

  const itemIdPrefix = 'inscriptionCampJour_enfant';
  for (let childIndex = 0; childIndex < n; childIndex++) {
    const fieldId = `enfant${childIndex + 1}_semaines`;
    const fromForm = formData && formData[fieldId] && typeof formData[fieldId] === 'object'
      ? { ...formData[fieldId] }
      : {};

    const childPrefix = `${itemIdPrefix}${childIndex + 1}`;
    const campJourItemsForChild = safeCart.filter(
      item => item?.type === CartItemType.CampJour && item?.id && (item.id === childPrefix || item.id.startsWith(`${childPrefix}_`))
    );

    campJourItemsForChild.forEach((campJourItem) => {
      const trajets = campJourItem?.inscriptionCamp?.trajetsAutobus ?? campJourItem?.trajetsAutobus;
      if (Array.isArray(trajets)) {
        trajets.forEach((t) => {
          const weekId = t.idProduitAge;
          const trajetValue = t.idProduitPrixTrajet || t.idProduitTrajet || t.trajet || null;
          if (weekId != null) {
            fromForm[weekId] = { selected: true, trajet: trajetValue };
          }
        });
      }
    });

    result[childIndex] = fromForm;
  }

  return result;
}
